import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Minus } from "iconsax-reactjs";
import { Calendar, ChevronDown, X } from "lucide-react";
import dayjs from "dayjs";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import MultiSelectDropdown from "./MultiSelectDropdown";
import KybComplianceRequestSuccessModal from "./KybComplianceRequestSuccessModal";
import { KybComplianceDetail } from "../types";
import { useParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { IkybAddProps, kybAdditionalUpdateDataApi } from "../../../shared/api/kyb";
import { toast } from "sonner";
import { OptionGeneratorFunc } from "../constants/stats";
import { InfoLoader } from "./KybComplianceRequestAdditionalInfoModal";

interface KybComplianceRequestInformationUpdateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: KybComplianceDetail;
  businessCatalogue: any;
  businessCatalogueLoading: boolean
}

interface InfoBlock {
  id: string;
  type: string;
  selectedSubIds: string[];
  selectedFields: string[];
}

const CATEGORY_TYPES = [
  { label: "Business details", value: "BUSINESS_DETAILS" },
  { label: "Contact information", value: "CONTACT_INFORMATION" },
  { label: "Compliance", value: "COMPLIANCE" },
  { label: "Wallet information", value: "WALLET_INFORMATION" },
  { label: "Directors", value: "DIRECTOR" },
  { label: "UBO", value: "UBO" },
];

const KybComplianceRequestInformationUpdateModal = ({
  open,
  onOpenChange,
  detail,
  businessCatalogue,
  businessCatalogueLoading
}: KybComplianceRequestInformationUpdateModalProps) => {
  const [infoBlocks, setInfoBlocks] = React.useState<InfoBlock[]>([
    { id: "1", type: "", selectedSubIds: [], selectedFields: [] },
  ]);
  const [dueDate, setDueDate] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);


  const { id = "" } = useParams();

  const { mutateAsync: handleAddKybInfoFunc } = useMutation({
    mutationFn: (data: IkybAddProps['data']) => kybAdditionalUpdateDataApi({ businessId: id, data }),
    mutationKey: ["add-kyb-information"],
    onSettled: (data, error: any) => {
      if (error) {
        console.error("Error submitting Request Information Update:", error);
        const errorRes = error?.response?.data?.message
        if (typeof errorRes === "string") {
          toast.error(errorRes)
        } else {
          toast.error(errorRes?.[0])
        }
      }
      if (data) {
        setShowSuccess(true);
      }
    }
  })


  const { groups } = React.useMemo(() => OptionGeneratorFunc({ businessCatalogue, detail }), [businessCatalogue, detail]);

  const directorSubOptions = React.useMemo(() => {
    return (detail?.directors ?? []).map((d, index) => ({
      label: `${d.firstName} ${d.lastName}`.trim() || `Director ${index + 1}`,
      value: d.email || d.bvnNumber || `director-${index}`,
    }));
  }, [detail]);

  const uboSubOptions = React.useMemo(() => {
    return (detail?.ubos ?? []).map((u, index) => ({
      label: `${u.firstName} ${u.lastName}`.trim() || `UBO ${index + 1}`,
      value: u.email || u.bvnNumber || `ubo-${index}`,
    }));
  }, [detail]);

  const getSubOptions = (type: string) => {
    if (type === "DIRECTOR") return directorSubOptions;
    if (type === "UBO") return uboSubOptions;
    return [];
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting) return;
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setInfoBlocks([{ id: "1", type: "", selectedSubIds: [], selectedFields: [] }]);
      setDueDate("");
      setShowSuccess(false);
    }
  };

  const handleAddBlock = () => {
    setInfoBlocks([
      ...infoBlocks,
      { id: Math.random().toString(), type: "", selectedSubIds: [], selectedFields: [] },
    ]);
  };

  const handleRemoveBlock = (id: string) => {
    setInfoBlocks(infoBlocks.filter((b) => b.id !== id));
  };

  const handleBlockTypeChange = (id: string, type: string) => {
    setInfoBlocks(
      infoBlocks.map((b) =>
        b.id === id
          ? { ...b, type, selectedSubIds: [], selectedFields: [] }
          : b,
      ),
    );
  };

  const handleBlockSubIdsChange = (id: string, selectedSubIds: string[]) => {
    setInfoBlocks(
      infoBlocks.map((b) => (b.id === id ? { ...b, selectedSubIds } : b)),
    );
  };

  const handleBlockFieldsChange = (id: string, selectedFields: string[]) => {
    setInfoBlocks(
      infoBlocks.map((b) => (b.id === id ? { ...b, selectedFields } : b)),
    );
  };

  const hasSelection = infoBlocks.some(
    (b) =>
      b.type !== "" &&
      b.selectedFields.length > 0 &&
      (!["DIRECTOR", "UBO"].includes(b.type) || b.selectedSubIds.length > 0),
  );

  const handleSubmit = async () => {
    if (!hasSelection || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const items: Array<{
        subtype: string;
        key: string;
        targetEmail?: string;
      }> = [];

      infoBlocks.forEach((block) => {
        if (!block.type) return;

        if (["DIRECTOR", "UBO"].includes(block.type)) {
          const personList =
            block.type === "DIRECTOR"
              ? (detail?.directors ?? [])
              : (detail?.ubos ?? []);

          block.selectedSubIds.forEach((subId) => {
            const foundPerson = personList.find(
              (p, idx) =>
                p.email === subId ||
                p.bvnNumber === subId ||
                `${block.type.toLowerCase()}-${idx}` === subId,
            );
            const targetEmail =
              foundPerson?.email ||
              (subId.includes("@") ? subId : `${block.type.toLowerCase()}@example.com`);

            block.selectedFields.forEach((fieldKey) => {
              items.push({
                subtype: block.type,
                targetEmail,
                key: fieldKey.split(":")?.[1]!,
              });
            });
          });
        } else {
          block.selectedFields.forEach((fieldKey) => {
            items.push({
              subtype: block.type,
              key: fieldKey.split(":")?.[1]!,
            });
          });
        }
      });

      const payload: IkybAddProps['data'] = {
        items,
      };
      if (dueDate) payload.dueDate = dayjs(dueDate).toISOString();

      await handleAddKybInfoFunc(payload);
    } catch (error) {
      console.error("Error submitting Request Information Update:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const dateInputRef = React.useRef<HTMLInputElement>(null);

  if (showSuccess) {
    return (
      <KybComplianceRequestSuccessModal
        open={open}
        onOpenChange={handleOpenChange}
      />
    );
  }

  const groupOptions = (type: string) => groups.find((group) => group.key === type)?.options;

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[600px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-2 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Request information update
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="py-6 space-y-4">
        {businessCatalogueLoading ? <InfoLoader /> : <>
          <div className="space-y-6">
            {infoBlocks.map((block, idx) => (
              <div
                key={block.id}
                className="relative space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-[450] text-[var(--text-30)] select-none">
                    Information {idx + 1}
                  </span>
                  {infoBlocks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveBlock(block.id)}
                      className="flex items-center gap-2 text-[13px] font-[450]"
                    >
                      <Minus />  Remove
                    </button>
                  )}
                </div>

                <div className="">
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        className="flex h-14 w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-4 py-1.5 text-left focus:outline-none cursor-pointer transition-colors hover:bg-[#E2E6F5]"
                      >
                        <div className="flex flex-col justify-center flex-1 min-w-0 pr-2">
                          <span className="text-[12px] font-[500] text-[#6F739B] leading-tight select-none">
                            Type
                          </span>
                          <span
                            className={`text-[14px] font-[400] truncate mt-0.5 ${block.type ? "text-[#161827]" : "text-[#737AA8]"
                              }`}
                          >
                            {CATEGORY_TYPES.find((item) => item.value === block.type)?.label ||
                              "Select type"}
                          </span>
                        </div>
                        <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        align="start"
                        sideOffset={6}
                        className="z-[125] max-h-[220px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1 shadow-lg"
                      >
                        {groups.map((type) => (
                          <DropdownMenu.Item
                            key={type.title}
                            onSelect={() => handleBlockTypeChange(block.id, type.key!)}
                            className={`cursor-pointer rounded-[8px] px-3 py-2 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${block.type === type.key
                              ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                              : "text-[#383B56]"
                              }`}
                          >
                            {type.title}
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>

                <div className="flex gap-2">
                  {["DIRECTOR", "UBO"].includes(block.type) && (
                    <div className="flex-1 min-w-0">
                      <MultiSelectDropdown
                        options={getSubOptions(block.type)}
                        selectedValues={block.selectedSubIds}
                        onChange={(vals) => handleBlockSubIdsChange(block.id, vals)}
                        label={block.type === "DIRECTOR" ? "Directors" : "Select UBOs"}
                        placeholder={block.type === "DIRECTOR" ? "Select directors" : "Select UBOs"}
                        hasAllOption={true}
                      />
                    </div>
                  )}

                  {block.type && (
                    <div className="w-full flex-1 min-w-0">
                      <MultiSelectDropdown
                        options={groupOptions(block.type) ?? []}
                        selectedValues={block.selectedFields}
                        onChange={(vals) => handleBlockFieldsChange(block.id, vals)}
                        label="Required information"
                        placeholder="Required information"
                        hasAllOption={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleAddBlock}
            className="inline-flex hover:bg-transparent py-0 items-center gap-2 text-base font-[450] text-[var(--primary)]"
          >
            <span>+</span>  <span>Add</span>
          </Button>

          <div className="pt-4 border-t space-y-1">
            <p className="text-sm font-[450] text-[var(--text-10)]">
              Due date <span className="font-[400] text-[#7E83AD] italic">• Optional</span>
            </p>
            <div className="relative">
              <input
                ref={dateInputRef}
                type="date"
                className="absolute inset-0 w-full h-full opacity-0 pointer-events-none z-10"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
              <div
                onClick={() => dateInputRef.current?.showPicker()}
                className="flex h-14 w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-5 text-left border border-transparent cursor-pointer"
              >
                <span
                  className={`text-[14px] font-[400] ${dueDate ? "text-[#161827]" : "text-[#737AA8]"
                    }`}
                >
                  {dueDate ? dayjs(dueDate).format("MMM D, YYYY") : "Select date..."}
                </span>
                <Calendar size={20} className="text-[#6F739B]" />
              </div>
            </div>
          </div>
        </>}
      </ModalBody>

      <ModalFooter className="flex items-center justify-end gap-3 pt-4 border-t border-[#EBEDF9] px-6 pb-6">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="h-11 px-5 text-[15px] font-[500] text-[#383B56] hover:bg-[#F5F6FD] rounded-[10px] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <Button
          type="button"
          disabled={!hasSelection || isSubmitting}
          onClick={handleSubmit}
          className="h-11 rounded-[10px] px-6 text-[15px] font-[500] bg-[#161827] text-white hover:bg-[#2B2F49] disabled:bg-[#9A9CC0] disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Sending..." : "Send request"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default KybComplianceRequestInformationUpdateModal;
